import { z } from "zod";

/**
 * Request/response shapes for docs/03-API-SPEC.md §3 (staff auth) and the `/users` endpoints from
 * §4. Imported by both apps/api (server-side validation) and apps/dashboard (client-side forms) —
 * one definition, no drift, per CLAUDE.md.
 */

const emailField = z.string().trim().toLowerCase().email();
const totpCodeField = z
  .string()
  .regex(/^\d{6}$/, "Enter the 6-digit code from your authenticator app");

export const StaffRoleSchema = z.enum([
  "SUPERADMIN",
  "PROGRAMME_ADMIN",
  "EDITOR",
  "REVIEWER",
  "ANALYST",
]);
export type StaffRoleValue = z.infer<typeof StaffRoleSchema>;

// ─────────────────────────────────────────────────────────────
// Login / TOTP
// ─────────────────────────────────────────────────────────────

export const LoginRequestSchema = z.object({
  email: emailField,
  password: z.string().min(1),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

export const TotpVerifyRequestSchema = z.object({
  challengeId: z.string().min(1),
  code: totpCodeField,
});
export type TotpVerifyRequest = z.infer<typeof TotpVerifyRequestSchema>;

export const TotpSetupRequestSchema = z.object({
  challengeId: z.string().min(1),
});
export type TotpSetupRequest = z.infer<typeof TotpSetupRequestSchema>;

export const TotpConfirmRequestSchema = z.object({
  challengeId: z.string().min(1),
  code: totpCodeField,
});
export type TotpConfirmRequest = z.infer<typeof TotpConfirmRequestSchema>;

// ─────────────────────────────────────────────────────────────
// Password lifecycle
// ─────────────────────────────────────────────────────────────

// Length only — argon2id's own cost parameters carry the real strength; a composition rule
// ("must contain a symbol") just pushes people toward predictable substitutions.
const passwordField = z.string().min(12, "Must be at least 12 characters");

export const PasswordForgotRequestSchema = z.object({ email: emailField });
export type PasswordForgotRequest = z.infer<typeof PasswordForgotRequestSchema>;

export const PasswordResetRequestSchema = z.object({
  token: z.string().min(1),
  newPassword: passwordField,
});
export type PasswordResetRequest = z.infer<typeof PasswordResetRequestSchema>;

export const PasswordChangeRequestSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: passwordField,
});
export type PasswordChangeRequest = z.infer<typeof PasswordChangeRequestSchema>;

// ─────────────────────────────────────────────────────────────
// /users CRUD and role assignment (docs/03-API-SPEC.md §4)
// ─────────────────────────────────────────────────────────────

export const UserCreateRequestSchema = z.object({
  email: emailField,
  fullName: z.string().trim().min(1).max(200),
  // No email service exists until Phase 7, so there is no "invite" flow yet — the creating
  // superadmin sets an initial password directly, same as the seed script does for the first
  // superadmin. Revisit once transactional email exists.
  initialPassword: passwordField,
  isSuperadmin: z.boolean().default(false),
  canViewPii: z.boolean().default(false),
});
export type UserCreateRequest = z.infer<typeof UserCreateRequestSchema>;

export const UserUpdateRequestSchema = z.object({
  fullName: z.string().trim().min(1).max(200).optional(),
  isActive: z.boolean().optional(),
  canViewPii: z.boolean().optional(),
  // Promoting/demoting superadmin status. Gated in the route handler, not here: only a superadmin
  // can reach this endpoint at all (requireSuperadmin()), and a superadmin already has "everything"
  // per docs/01-PRODUCT-SPEC.md §2 — this is the mechanism for onboarding a second one.
  isSuperadmin: z.boolean().optional(),
});
export type UserUpdateRequest = z.infer<typeof UserUpdateRequestSchema>;

export const UserRoleAssignRequestSchema = z.object({
  programmeId: z.string().min(1),
  // null removes the user's role on that programme. SUPERADMIN is deliberately excluded here —
  // it is a global flag (`User.isSuperadmin`, set via PATCH /users/:id), not a per-programme role,
  // matching "Global" scope in docs/01-PRODUCT-SPEC.md §2's roles table.
  role: z.enum(["PROGRAMME_ADMIN", "EDITOR", "REVIEWER", "ANALYST"]).nullable(),
});
export type UserRoleAssignRequest = z.infer<typeof UserRoleAssignRequestSchema>;
